import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export const PrivacyPolicy = ({ onClose }) => (
    <div className="h-full flex flex-col bg-[#e8e6df] text-[#333] font-sans select-text">
        <div className="h-10 bg-[#dcdad3] border-b border-[#bdbaa8] flex items-center justify-between px-4 shadow-sm shrink-0">
            <span className="text-xs font-bold uppercase tracking-widest text-[#555]">Privacy Policy</span>
            <button onClick={onClose} className="hover:bg-[#bdbaa8] p-1 rounded">
                <X size={14} />
            </button>
        </div>
        <div className="flex-1 overflow-y-auto p-8 prose prose-sm max-w-none">
            <h1 className="text-xl font-bold uppercase tracking-widest mb-6">Privacy Policy for WELCOME TO PARADISE, a Product of PACIFIC INTERLUDE.</h1>

            <h3 className="font-bold mt-6 mb-2">1. Introduction</h3>
            <p>At PACIFIC INTERLUDE, accessible from https://www.welcometoparadise.world one of our main priorities is the privacy of our visitors and subscribers. This Privacy Policy outlines the types of information we collect, how we use it, and the rights you have regarding your personal data.</p>
            <p>This Privacy Policy applies only to online activities on welcometoparadise.world, including website visitors and subscribers who create accounts or purchase digital magazine subscriptions. This policy does not apply to information collected offline or through other channels.</p>
            <p>If you have any questions about this policy, please contact us at <a href="mailto:work@pacificinterlude.com" className="text-[#AF2E1B] hover:underline">work@pacificinterlude.com</a>.</p>

            <h3 className="font-bold mt-6 mb-2">2. Consent</h3>
            <p>By accessing or using our website, you agree to this Privacy Policy and consent to the collection and use of your information as described.</p>

            <h3 className="font-bold mt-6 mb-2">3. Information We Collect</h3>
            <p>We collect different types of personal information, including:</p>
            <ul className="list-disc pl-5 space-y-1">
                <li><strong>Account & Subscription Data:</strong> Name, email address, payment details (processed through third-party payment providers), billing address.</li>
                <li><strong>Contact Data:</strong> If you contact us, we may collect your name, email, phone number, and message details.</li>
                <li><strong>Automatically Collected Data:</strong> IP address, browser type, device identifiers, and website interaction data through cookies and analytics tools.</li>
            </ul>

            <h3 className="font-bold mt-6 mb-2">4. How We Use Your Information</h3>
            <p>We use your information for purposes including:</p>
            <ul className="list-disc pl-5 space-y-1">
                <li>Providing and managing your account and subscriptions.</li>
                <li>Processing payments (via third-party payment processors).</li>
                <li>Personalizing your experience and improving our website.</li>
                <li>Sending emails about updates, promotions, or service-related messages.</li>
                <li>Detecting and preventing fraud.</li>
            </ul>

            <h3 className="font-bold mt-6 mb-2">5. Payment Information</h3>
            <p>We use third-party payment processors (e.g., Stripe, PayPal) to handle transactions securely. We do not store or process your credit card details directly. Payment processors may collect your billing information and are responsible for their own data handling policies. Please review their privacy policies for more information.</p>

            <h3 className="font-bold mt-6 mb-2">6. Log Files & Tracking Technologies</h3>
            <p>We use log files and tracking technologies (e.g., Google Analytics, Facebook Pixel) to analyze site usage and improve our services. These may collect:</p>
            <ul className="list-disc pl-5 space-y-1">
                <li>IP addresses, browser type, ISP, and timestamps.</li>
                <li>Pages visited, clicks, and referral URLs.</li>
            </ul>
            <p>You can opt out of tracking via Google’s opt-out tool or your browser settings.</p>

            <h3 className="font-bold mt-6 mb-2">7. Cookies & Do Not Track (DNT)</h3>
            <p>We use cookies to personalize content, improve website functionality, and serve targeted ads. You can manage or disable cookies in your browser settings.</p>
            <p><strong>Do Not Track Requests:</strong> We do not respond to browser “Do Not Track” signals due to industry-wide limitations.</p>
            <p>For more information, visit our Cookie Policy.</p>

            <h3 className="font-bold mt-6 mb-2">8. Third-Party Sharing & Advertising</h3>
            <p>We partner with third-party advertisers and analytics providers who may collect data via cookies, web beacons, or other tracking technologies.</p>
            <p><strong>Opt-Out Options:</strong></p>
            <ul className="list-disc pl-5 space-y-1">
                <li><strong>California Residents:</strong> You can opt out of targeted advertising and data sharing via our “Do Not Sell or Share My Personal Information” link here.</li>
                <li><strong>General Users:</strong> Adjust your browser’s cookie settings or visit Network Advertising Initiative’s opt-out page.</li>
            </ul>

            <h3 className="font-bold mt-6 mb-2">9. California Consumer Privacy Rights (CCPA/CPRA)</h3>
            <p>Under California law, you have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
                <li><strong>Access:</strong> Request details about the personal data we collect.</li>
                <li><strong>Delete:</strong> Ask us to delete your data, subject to certain legal exceptions.</li>
                <li><strong>Opt-Out:</strong> Prevent the sale or sharing of your data with third parties.</li>
                <li><strong>Correct:</strong> Request corrections to inaccurate personal data.</li>
                <li><strong>Limit Sensitive Data Use:</strong> Restrict how we use sensitive personal information (e.g., payment details).</li>
            </ul>
            <p className="mt-2"><strong>How to Exercise Your Rights:</strong></p>
            <p>To submit a request, email us at <a href="mailto:work@pacificinterlude.com" className="text-[#AF2E1B] hover:underline">work@pacificinterlude.com</a>. We will respond within 30 days.</p>

            <h3 className="font-bold mt-6 mb-2">10. GDPR Rights (For EU Users Only)</h3>
            <p>If you are in the European Economic Area (EEA), you have additional data rights under GDPR, including access, correction, deletion, and data portability.</p>
            <p>If you have any questions, contact <a href="mailto:work@pacificinterlude.com" className="text-[#AF2E1B] hover:underline">work@pacificinterlude.com</a></p>

            <h3 className="font-bold mt-6 mb-2">11. Age Restriction & Children's Privacy</h3>
            <p>This website is intended for users aged 18 and older. We do not knowingly collect data from children under 13 (or under 16 in certain jurisdictions). If we discover such data, we will delete it immediately. If you believe a minor has provided us with information, please contact us at <a href="mailto:work@pacificinterlude.com" className="text-[#AF2E1B] hover:underline">work@pacificinterlude.com</a>.</p>

            <h3 className="font-bold mt-6 mb-2">12. Changes to This Privacy Policy</h3>
            <p>We may update this Privacy Policy from time to time. The latest version will always be posted at https://www.welcometoparadise.world</p>
            <p>If there are significant changes, we will notify users by email or website notice.</p>

            <h3 className="font-bold mt-6 mb-2">13. Contact Us</h3>
            <p>For questions about this Privacy Policy or your privacy rights, contact us at:</p>
            <p>📧 Email: <a href="mailto:work@pacificinterlude.com" className="text-[#AF2E1B] hover:underline">work@pacificinterlude.com</a></p>
            <p>📍 Mail: Pacific interlude 1019 pine st, santa monica ca 90405</p>
        </div>
    </div>
);

export const TermsOfService = ({ onClose }) => (
    <div className="h-full flex flex-col bg-[#e8e6df] text-[#333] font-sans select-text">
        <div className="h-10 bg-[#dcdad3] border-b border-[#bdbaa8] flex items-center justify-between px-4 shadow-sm shrink-0">
            <span className="text-xs font-bold uppercase tracking-widest text-[#555]">Terms of Service</span>
            <button onClick={onClose} className="hover:bg-[#bdbaa8] p-1 rounded">
                <X size={14} />
            </button>
        </div>
        <div className="flex-1 overflow-y-auto p-8 prose prose-sm max-w-none">
            <h1 className="text-xl font-bold uppercase tracking-widest mb-6">1. AGREEMENT TO TERMS</h1>
            <p>These Terms of Use constitute a legally binding agreement between you, whether personally or on behalf of an entity ("you") and PACIFIC INTERLUDE ("Company," "we," "us," or "our"), concerning your access to and use of the http://www.welcometoparadise.world website, as well as any other media forms, media channels, mobile websites, or mobile applications related, linked, or otherwise connected thereto (collectively, the "Site").</p>
            <p>We are a company registered in the State of California, United States. By accessing the Site, you confirm that you have read, understood, and agreed to be bound by these Terms of Use. If you do not agree to these terms, you are prohibited from using the Site and must discontinue use immediately.</p>
            <p>Supplemental terms and conditions may be posted on the Site periodically and are expressly incorporated into these Terms of Use. We reserve the right, at our sole discretion, to modify or update these Terms at any time. Changes will be reflected in the “Last Updated” date, and your continued use of the Site constitutes acceptance of the updated Terms.</p>
            <p>The Site is intended for users at least 18 years old. Persons under the age of 18 are prohibited from using or registering for the Site.</p>

            <h3 className="font-bold mt-6 mb-2">2. INTELLECTUAL PROPERTY RIGHTS</h3>
            <p>Unless otherwise indicated, the Site and all source code, databases, functionality, software, website designs, text, images, graphics, and other content (collectively, the "Content") and trademarks, service marks, and logos (the "Marks") are owned or licensed by us and protected under U.S. and international copyright and trademark laws. The Content and Marks are provided "as is" for your personal, non-commercial use only.</p>
            <p>You may not copy, reproduce, republish, upload, post, publicly display, encode, translate, transmit, distribute, sell, or license any portion of the Site or Content without our express written permission.</p>

            <h3 className="font-bold mt-6 mb-2">3. USER REPRESENTATIONS</h3>
            <p>By using the Site, you represent and warrant that:</p>
            <ul className="list-disc pl-5 space-y-1">
                <li>You provide accurate, complete, and current registration information.</li>
                <li>You will maintain and update your information as necessary.</li>
                <li>You have the legal capacity to comply with these Terms.</li>
                <li>You are not a minor in your jurisdiction.</li>
                <li>You will not access the Site through automated or non-human means (bots, scripts, etc.).</li>
                <li>You will not use the Site for unlawful purposes.</li>
                <li>Your use of the Site will not violate any applicable laws or regulations.</li>
            </ul>
            <p className="mt-2">If any information you provide is false, inaccurate, outdated, or incomplete, we have the right to suspend or terminate your account.</p>

            <h3 className="font-bold mt-6 mb-2">4. USER REGISTRATION</h3>
            <p>You may be required to register for an account. You agree to keep your password confidential and are responsible for all activities under your account. We reserve the right to remove, reclaim, or change any username deemed inappropriate.</p>

            <h3 className="font-bold mt-6 mb-2">5. PRODUCTS</h3>
            <p>We make every effort to display accurate descriptions, colors, features, and specifications of the products on the Site. However, we cannot guarantee that your device’s display accurately reflects product colors or details. Products are subject to availability, and we reserve the right to discontinue items at any time. Prices are subject to change.</p>

            <h3 className="font-bold mt-6 mb-2">6. PURCHASES AND PAYMENTS</h3>
            <p>We accept the following payment methods:</p>
            <ul className="list-disc pl-5 space-y-1">
                <li>Visa</li>
                <li>MasterCard</li>
                <li>American Express</li>
                <li>Discover</li>
                <li>PayPal</li>
            </ul>
            <p className="mt-2">You agree to provide current, complete, and accurate purchase and account information. Sales tax will be added where required by law. All payments must be in U.S. dollars.</p>
            <p>If your order is subject to recurring charges (such as a subscription), you consent to automatic charges to your payment method until you cancel. For California residents, this complies with the California Auto Renewal Law (ARL), and you will be notified of any automatic renewals with clear cancellation instructions.</p>
            <p>We reserve the right to correct any pricing errors even if payment has already been processed. Orders may be limited or canceled at our discretion.</p>
        </div>
    </div>
);

export const ContactUs = ({ onClose }) => (
    <div className="h-full flex flex-col bg-[#e8e6df] text-[#333] font-sans select-text">
        <div className="h-10 bg-[#dcdad3] border-b border-[#bdbaa8] flex items-center justify-between px-4 shadow-sm shrink-0">
            <span className="text-xs font-bold uppercase tracking-widest text-[#555]">Contact Us</span>
            <button onClick={onClose} className="hover:bg-[#bdbaa8] p-1 rounded">
                <X size={14} />
            </button>
        </div>
        <div className="flex-1 overflow-y-auto p-8 prose prose-sm max-w-none flex flex-col items-center justify-center text-center">
            <h1 className="text-xl font-bold uppercase tracking-widest mb-6">Contact Us</h1>
            <p className="mb-4 max-w-md">
                If you have any questions or any general inquiry, please contact us at <a href="mailto:work@pacificinterlude.com" className="text-[#AF2E1B] hover:underline">work@pacificinterlude.com</a>
            </p>
            <p className="mb-4 text-sm text-[#666]">
                We typically respond within 5 business days.
            </p>
            <p className="font-bold">Thanks!</p>
        </div>
    </div>
);

export const CustomerAgreement = ({ onClose }) => (
    <div className="h-full flex flex-col bg-[#e8e6df] text-[#333] font-sans select-text">
        <div className="h-10 bg-[#dcdad3] border-b border-[#bdbaa8] flex items-center justify-between px-4 shadow-sm shrink-0">
            <span className="text-xs font-bold uppercase tracking-widest text-[#555]">Customer Agreement</span>
            <button onClick={onClose} className="hover:bg-[#bdbaa8] p-1 rounded">
                <X size={14} />
            </button>
        </div>
        <div className="flex-1 overflow-y-auto p-8 prose prose-sm max-w-none">
            <h1 className="text-xl font-bold uppercase tracking-widest mb-6">WELCOME TO PARADISE CUSTOMER AGREEMENT</h1>
            <p className="mb-4">Last Updated: November 21st, 2025</p>
            <p>This Customer Agreement ("Agreement") governs your use of WELCOME TO PARADISE, a digital magazine and content platform available at [welcometoparadise.world] (the "Website"). By subscribing to WELCOME TO PARADISE, you agree to the following terms:</p>

            <h3 className="font-bold mt-6 mb-2">1. Subscription & Payment Terms</h3>
            <p>1.1. WELCOME TO PARADISE offers 1 subscription plans:</p>
            <ul className="list-disc pl-5 space-y-1">
                <li>Monthly Plan: $24.99 per month</li>
            </ul>
            <p>1.2. Payment is processed via debit or credit card at the time of purchase and renews automatically unless canceled prior to the next billing cycle.</p>
            <p>1.3. You authorize WELCOME TO PARADISE to charge your chosen payment method for recurring subscription fees unless canceled.</p>

            <h3 className="font-bold mt-6 mb-2">2. Cancellation & No Refund Policy</h3>
            <p>2.1. You may cancel your subscription at any time through your account settings.</p>
            <p>2.2. Upon cancellation, you will retain access to WELCOME TO PARADISE’s content until the end of your current billing cycle, after which access will be revoked.</p>
            <p>2.3. All payments are final, and WELCOME TO PARADISE does not offer refunds, credits, or prorated adjustments for cancellations.</p>

            <h3 className="font-bold mt-6 mb-2">3. User Responsibilities</h3>
            <p>3.1. Subscribers may access WELCOME TO PARADISE’s digital content for personal use only.</p>
            <p>3.2. You may not share, resell, distribute, or otherwise make WELCOME TO PARADISE’s paywalled content available to third parties.</p>
            <p>3.3. Any violation of these terms may result in immediate suspension or termination of your account without refund.</p>

            <h3 className="font-bold mt-6 mb-2">4. Dispute Resolution</h3>
            <p>4.1. If you have any complaints or disputes regarding your subscription, please contact WELCOME TO PARADISE customer support at <a href="mailto:work@pacificinterlude.com" className="text-[#AF2E1B] hover:underline">work@pacificinterlude.com</a>.</p>
        </div>
    </div>
);

export const CookieConsent = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const consent = localStorage.getItem('cookie_consent');
        if (!consent) {
            setIsVisible(true);
        }
    }, []);

    const acceptCookies = () => {
        localStorage.setItem('cookie_consent', 'true');
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-[#e8e6df] border border-[#bdbaa8] shadow-[0_10px_30px_rgba(0,0,0,0.2)] z-[9999] p-4 flex flex-col gap-3 rounded-sm">
            <div className="flex items-start justify-between">
                <h4 className="text-xs font-bold uppercase tracking-widest text-[#333]">Cookie Notice</h4>
                <button onClick={() => setIsVisible(false)} className="text-[#666] hover:text-[#333]">
                    <X size={14} />
                </button>
            </div>
            <p className="text-xs text-[#555] leading-relaxed">
                We use cookies to enhance your experience and analyze our traffic. By continuing to use our site, you consent to our use of cookies.
            </p>
            <div className="flex justify-end gap-2">
                <button
                    onClick={() => setIsVisible(false)}
                    className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#666] hover:bg-[#dcdad3] rounded-sm transition-colors"
                >
                    Decline
                </button>
                <button
                    onClick={acceptCookies}
                    className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white bg-[#AF2E1B] hover:bg-[#CC6324] rounded-sm shadow-sm transition-colors"
                >
                    Accept
                </button>
            </div>
        </div>
    );
};
